import { useEffect, useState } from "react";
import { UserIdentity } from "@supabase/auth-js";
import { toast } from "react-toastify";

import useUserStore from "../../stores/user";
import supabase from "../../utils/supabase";
import { useT } from "../../stores/uiLang";

// Run E2 J2: the whole dialog in English and Spanish, from the shared UI language store.
const SETTINGS_T = {
  en: {
    instructorTitle: "Instructor Settings",
    adminTitle: "Admin Settings",
    notifHeading: "Notification Preferences",
    assignmentsLabel: "Assignment Submissions",
    assignmentsHelp: "Get notified when students submit assignments",
    lmsHeading: "LMS Integration Status",
    lastSync: "Last sync:",
    notConnected: "Not connected",
    connected: "Connected",
    connect: "Connect",
    cancel: "Cancel",
    save: "Save Settings",
    saving: "Saving...",
    linkError: "Error linking with Google Classroom",
    saveError: "Failed to save settings",
    saveOk: "Settings saved successfully",
    close: "Close",
  },
  es: {
    instructorTitle: "Configuración del maestro",
    adminTitle: "Configuración del administrador",
    notifHeading: "Preferencias de notificaciones",
    assignmentsLabel: "Entregas de tareas",
    assignmentsHelp: "Recibe un aviso cuando tus estudiantes entreguen tareas",
    lmsHeading: "Estado de la integración con el LMS",
    lastSync: "Última sincronización:",
    notConnected: "No conectado",
    connected: "Conectado",
    connect: "Conectar",
    cancel: "Cancelar",
    save: "Guardar configuración",
    saving: "Guardando...",
    linkError: "Error al conectar con Google Classroom",
    saveError: "No se pudo guardar la configuración",
    saveOk: "Configuración guardada",
    close: "Cerrar",
  },
};

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  showNotifications?: boolean;
}

export default function SettingsModal({
  isOpen,
  onClose,
  title,
  showNotifications = true,
}: SettingsModalProps) {
  const t = useT(SETTINGS_T);
  const user = useUserStore((state) => state.user);
  const profile = useUserStore((state) => state.profile);
  const { fetchUserProfile } = useUserStore();
  const [identities, setIdentities] = useState<UserIdentity[]>([]);
  // Run E2: chat removed (no "messages" notification setting any more).
  const [notificationSettings, setNotificationSettings] = useState({
    assignments: true,
  });
  const [loadingSettings, setLoadingSettings] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    getIdentities();
  }, [user?.id]);

  // Update notification settings when profile is loaded
  useEffect(() => {
    if (profile) {
      setNotificationSettings({
        assignments: profile.notifications?.assignments ?? true,
      });
    }
  }, [profile]);

  if (!isOpen) return null;

  async function linkWithGoogleClassroom() {
    const { data, error } = await supabase.auth.linkIdentity({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/teacher/dashboard`,
        scopes:
          "email profile https://www.googleapis.com/auth/classroom.courses.readonly https://www.googleapis.com/auth/classroom.rosters.readonly https://www.googleapis.com/auth/classroom.profile.emails https://www.googleapis.com/auth/classroom.coursework.students https://www.googleapis.com/auth/classroom.coursework.me",
      },
    });

    if (error) {
      console.error("Error linking with Google Classroom:", error);
      toast.error(t.linkError);
    }

    if (data) {
      console.info("Successfully linked with Google Classroom");
    }
  }

  const saveNotificationSettings = async () => {
    setLoadingSettings(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          notifications: {
            assignments: notificationSettings.assignments,
          },
        })
        .eq("id", user.id)
        .select("*");

      if (error) {
        toast.error(t.saveError);
        return;
      }

      // Refresh the profile to get updated data
      await fetchUserProfile(user);

      toast.success(t.saveOk);
    } catch (error) {
      toast.error(t.saveError);
    } finally {
      setLoadingSettings(false);
    }
  };

  async function getIdentities() {
    const { data, error } = await supabase.auth.getUserIdentities();

    if (error) {
      console.error("Error getting identities:", error);
    }

    if (data) {
      setIdentities(data.identities || []);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="modal-content p-0 max-w-3xl bg-white rounded-lg shadow-md overflow-hidden max-h-[90vh] w-full flex flex-col">
        <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">{title === "Admin Settings" ? t.adminTitle : title ?? t.instructorTitle}</h3>
          <button
            onClick={onClose}
            aria-label={t.close}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          {showNotifications && (
            <section className="bg-white shadow-sm rounded-lg p-4 border border-gray-200">
              <h4 className="text-md font-medium text-gray-900 mb-3">
                {t.notifHeading}
              </h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-6 w-6 text-green-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        {t.assignmentsLabel}
                      </p>
                      <p className="text-sm text-gray-500">
                        {t.assignmentsHelp}
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={notificationSettings.assignments}
                      onChange={(e) =>
                        setNotificationSettings((prev) => ({
                          ...prev,
                          assignments: e.target.checked,
                        }))
                      }
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </section>
          )}

          {/* LMS Sync Status */}
          <section className="bg-white shadow-sm rounded-lg p-4 border border-gray-200">
            <h4 className="text-md font-medium text-gray-900 mb-3">
              {t.lmsHeading}
            </h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="bg-white p-2 rounded-lg border border-gray-200 mr-3">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M23.9977 12.1591C23.9977 18.7765 18.639 24.1352 12.0216 24.1352C5.40421 24.1352 0.0454712 18.7765 0.0454712 12.1591C0.0454712 5.54165 5.40421 0.182922 12.0216 0.182922C18.639 0.182922 23.9977 5.54165 23.9977 12.1591Z"
                        fill="#2A8CBF"
                      />
                      <path
                        d="M11.7863 6.52271H7.81744V17.9564H11.7863V6.52271Z"
                        fill="#EEEEEE"
                      />
                      <path
                        d="M16.4886 6.52271H12.5198V17.9564H16.4886V6.52271Z"
                        fill="#EEEEEE"
                      />
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      Google Classroom
                    </div>
                    <div className="text-xs text-gray-500">
                      {identities.some((id) => id.provider === "google")
                        ? `${t.lastSync} ${new Date().toLocaleString()}`
                        : t.notConnected}
                    </div>
                  </div>
                </div>
                {identities.some((id) => id.provider === "google") ? (
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    {t.connected}
                  </span>
                ) : (
                  <button
                    onClick={linkWithGoogleClassroom}
                    className="px-2 py-1 border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-50"
                  >
                    {t.connect}
                  </button>
                )}
              </div>
            </div>
          </section>
          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button onClick={onClose} className="btn btn-small btn-secondary">
              {t.cancel}
            </button>
            <button
              onClick={saveNotificationSettings}
              disabled={loadingSettings}
              className="btn btn-small"
            >
              {loadingSettings ? t.saving : t.save}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
